import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    listHappeningTemplates,
    createHappeningTemplate,
    updateHappeningTemplate,
    deleteHappeningTemplate,
    listTaskDefinitions,
    createTaskDefinition,
    updateTaskDefinition,
    deleteTaskDefinition,
    listPlaces,
    listPeople,
    type HappeningTemplate,
    type HappeningTemplateCreate,
    type TaskDefinition,
    type TaskDefinitionCreate,
    type HappeningType,
    type TaskType,
    type TaskPriority,
    type TemplateRole,
    type RadioShowType,
    type Place,
    type Person,
    type HappeningArtist,
} from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Bell,
    Calendar,
    ChevronDown,
    ChevronRight,
    Clock,
    Edit,
    FileText,
    Layers,
    Mail,
    Plus,
    Radio,
    Trash2,
    UserIcon,
    Users,
    X,
} from 'lucide-react';
import { SearchFilter } from '@/components/SearchFilter';

const HAPPENING_TYPES: { value: HappeningType; label: string; icon: typeof Radio }[] = [
    { value: 'show', label: 'Radio Show', icon: Radio },
    { value: 'event', label: 'Event', icon: Users },
    { value: 'recording', label: 'Recording', icon: FileText },
    { value: 'festival', label: 'Festival', icon: Calendar },
    { value: 'mix', label: 'Mix', icon: Layers },
];

const TASK_TYPES: TaskType[] = ['booking', 'preparation', 'follow_up', 'technical'];
const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];
const PERSON_TYPES = ['artist', 'staff', 'guest', 'support'];

type TemplateFormState = {
    slug: string;
    name: string;
    description: string;
    happening_type: HappeningType;
    default_duration_minutes: number;
    required_roles: TemplateRole[];
    default_show_type?: RadioShowType; // Only for 'show' type templates
    default_place_id?: string;
    default_artists: HappeningArtist[];
    // Default reminder settings
    default_completion_reminder_offsets: number[];
    default_happening_reminder_offsets: number[];
    default_profile_reminder_offsets: number[];
    default_post_show_enabled: boolean;
    default_post_show_offset_hours: number;
};

type TaskDefFormState = {
    task_key: string;
    title: string;
    description: string;
    type: TaskType;
    priority: TaskPriority;
    due_offset_days: number;
    assignee_role: string;
    is_required: boolean;
    reminder_enabled: boolean;
    reminder_offset_hours: number;
};

const defaultTemplateForm = (): TemplateFormState => ({
    slug: '',
    name: '',
    description: '',
    happening_type: 'show',
    default_duration_minutes: 60,
    required_roles: [],
    default_show_type: undefined,
    default_place_id: '',
    default_artists: [],
    default_completion_reminder_offsets: [-120],
    default_happening_reminder_offsets: [-72],
    default_profile_reminder_offsets: [-120],
    default_post_show_enabled: true,
    default_post_show_offset_hours: 24,
});

const defaultTaskDefForm = (): TaskDefFormState => ({
    task_key: '',
    title: '',
    description: '',
    type: 'preparation',
    priority: 'medium',
    due_offset_days: 0,
    assignee_role: '',
    is_required: true,
    reminder_enabled: true,
    reminder_offset_hours: 0,
});

const formatOffsetHours = (hours: number): string => {
    const abs = Math.abs(hours);
    if (hours === 0) return 'On start time';
    const direction = hours < 0 ? 'before' : 'after';
    if (abs < 24) return `${abs} hour${abs !== 1 ? 's' : ''} ${direction}`;
    if (abs < 168) return `${Math.floor(abs / 24)} day${Math.floor(abs / 24) !== 1 ? 's' : ''} ${direction}`;
    return `${Math.floor(abs / 168)} week${Math.floor(abs / 168) !== 1 ? 's' : ''} ${direction}`;
};

const BEFORE_SHOW_OFFSET_OPTIONS = [
    { value: -168, label: '1 week before' },
    { value: -120, label: '5 days before' },
    { value: -72, label: '3 days before' },
    { value: -48, label: '2 days before' },
    { value: -24, label: '1 day before' },
    { value: -12, label: '12 hours before' },
    { value: -6, label: '6 hours before' },
    { value: -1, label: '1 hour before' },
];

const POST_SHOW_OFFSET_OPTIONS = [
    { value: 1, label: '1 hour after show' },
    { value: 6, label: '6 hours after show' },
    { value: 12, label: '12 hours after show' },
    { value: 24, label: '1 day after show' },
    { value: 48, label: '2 days after show' },
    { value: 72, label: '3 days after show' },
];

const slugify = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');

export default function TemplatesPage() {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const { data: places = [] } = useQuery<Place[]>({
        queryKey: ['places', 'all'],
        queryFn: () => listPlaces(),
        staleTime: 5 * 60 * 1000,
    });
    const { data: people = [] } = useQuery<Person[]>({
        queryKey: ['people', 'all'],
        queryFn: () => listPeople({ type: 'artist' }),
        staleTime: 5 * 60 * 1000,
    });
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<HappeningTemplate | null>(null);
    const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
    const [showTaskDefDialog, setShowTaskDefDialog] = useState(false);
    const [editingTaskDef, setEditingTaskDef] = useState<TaskDefinition | null>(null);
    const [showCopyTasksDialog, setShowCopyTasksDialog] = useState(false);
    const [selectedSourceTemplate, setSelectedSourceTemplate] = useState<string | null>(null);
    const [templateForm, setTemplateForm] = useState<TemplateFormState>(defaultTemplateForm);
    const [taskDefForm, setTaskDefForm] = useState<TaskDefFormState>(defaultTaskDefForm);
    const [newRoleInput, setNewRoleInput] = useState({ role: '', person_type: 'artist', min_count: 1, max_count: '' as string | number });
    const templateInitialRef = useRef<TemplateFormState | null>(null);
    const taskInitialRef = useRef<TaskDefFormState | null>(null);
    const [showTemplateUnsavedConfirm, setShowTemplateUnsavedConfirm] = useState(false);
    const [showTaskUnsavedConfirm, setShowTaskUnsavedConfirm] = useState(false);

    // Fetch templates
    const { data: templates = [], isLoading } = useQuery({
        queryKey: ['happening-templates'],
        queryFn: () => listHappeningTemplates({ is_active: true, include_system: true }),
    });

    // Fetch task definitions for expanded template
    const { data: taskDefinitions = [] } = useQuery({
        queryKey: ['task-definitions', expandedTemplateId],
        queryFn: () => (expandedTemplateId ? listTaskDefinitions(expandedTemplateId) : Promise.resolve([])),
        enabled: !!expandedTemplateId,
    });

    // Sort task definitions by due offset (earliest first)
    const sortedTaskDefinitions = useMemo(() => {
        return [...taskDefinitions].sort((a, b) => {
            const aOffset = (a.due_offset_days || 0) * 24 + (a.due_offset_hours || 0);
            const bOffset = (b.due_offset_days || 0) * 24 + (b.due_offset_hours || 0);
            return aOffset - bOffset;
        });
    }, [taskDefinitions]);

    // Filter templates
    const filteredTemplates = useMemo(() => {
        if (!searchQuery.trim()) return templates;
        const query = searchQuery.toLowerCase();
        return templates.filter(
            (t) =>
                t.name.toLowerCase().includes(query) ||
                t.slug.toLowerCase().includes(query) ||
                t.happening_type.toLowerCase().includes(query)
        );
    }, [templates, searchQuery]);

    // Get the template that the current task definition belongs to
    const currentTemplateForTaskDef = useMemo(() => {
        if (editingTaskDef) {
            // When editing, find the template by the task definition's template_id
            return templates.find(t => t.id === editingTaskDef.template_id);
        } else if (expandedTemplateId) {
            // When creating, use the expanded template
            return templates.find(t => t.id === expandedTemplateId);
        }
        return null;
    }, [templates, editingTaskDef, expandedTemplateId]);

    // Mutations
    const createMutation = useMutation({
        mutationFn: (data: HappeningTemplateCreate) => createHappeningTemplate(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happening-templates'] });
            setShowCreateDialog(false);
            setTemplateForm(defaultTemplateForm);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<HappeningTemplate> }) =>
            updateHappeningTemplate(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happening-templates'] });
            setEditingTemplate(null);
            setTemplateForm(defaultTemplateForm);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteHappeningTemplate(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happening-templates'] });
        },
    });

    const createTaskDefMutation = useMutation({
        mutationFn: (data: TaskDefinitionCreate) => createTaskDefinition(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task-definitions', expandedTemplateId] });
            setShowTaskDefDialog(false);
            setTaskDefForm(defaultTaskDefForm);
        },
    });

    const updateTaskDefMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<TaskDefinition> }) =>
            updateTaskDefinition(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task-definitions', expandedTemplateId] });
            setEditingTaskDef(null);
            setShowTaskDefDialog(false);
            setTaskDefForm(defaultTaskDefForm);
        },
    });

    const deleteTaskDefMutation = useMutation({
        mutationFn: (id: string) => deleteTaskDefinition(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task-definitions', expandedTemplateId] });
        },
    });

    // Copy tasks from another template
    const copyTasksMutation = useMutation({
        mutationFn: async ({ sourceTemplateId, targetTemplateId }: { sourceTemplateId: string; targetTemplateId: string }) => {
            const sourceTaskDefs = await listTaskDefinitions(sourceTemplateId);

            // Create new task definitions for target template
            for (const taskDef of sourceTaskDefs) {
                await createTaskDefinition({
                    template_id: targetTemplateId,
                    task_key: taskDef.task_key,
                    title: taskDef.title,
                    description: taskDef.description,
                    type: taskDef.type,
                    priority: taskDef.priority,
                    due_offset_days: taskDef.due_offset_days,
                    due_offset_hours: taskDef.due_offset_hours,
                    assignee_role: taskDef.assignee_role,
                    depends_on: taskDef.depends_on,
                    auto_complete_on_happening_status: taskDef.auto_complete_on_happening_status,
                    auto_create_on_happening_status: taskDef.auto_create_on_happening_status,
                    subtask_keys: taskDef.subtask_keys,
                    sort_order: taskDef.sort_order,
                    is_required: taskDef.is_required,
                    reminder_enabled: taskDef.reminder_enabled,
                    reminder_offset_hours: taskDef.reminder_offset_hours,
                    metadata: taskDef.metadata,
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task-definitions', expandedTemplateId] });
            setShowCopyTasksDialog(false);
            setSelectedSourceTemplate(null);
        },
    });

    // Handlers
    const handleCreateTemplate = (e: React.FormEvent) => {
        e.preventDefault();
        const metadata: Record<string, unknown> = {};
        if (templateForm.happening_type === 'show' && templateForm.default_show_type) {
            metadata.default_show_type = templateForm.default_show_type;
        }
        if (templateForm.default_place_id) {
            metadata.place_id = templateForm.default_place_id;
        }
        if (templateForm.default_artists.length > 0) {
            metadata.default_artists = templateForm.default_artists;
        }
        // Save reminder defaults
        metadata.default_completion_reminder_offsets = templateForm.default_completion_reminder_offsets;
        metadata.default_happening_reminder_offsets = templateForm.default_happening_reminder_offsets;
        metadata.default_profile_reminder_offsets = templateForm.default_profile_reminder_offsets;
        metadata.default_post_show_enabled = templateForm.default_post_show_enabled;
        metadata.default_post_show_offset_hours = templateForm.default_post_show_offset_hours;
        createMutation.mutate({
            slug: templateForm.slug || slugify(templateForm.name),
            name: templateForm.name,
            description: templateForm.description || undefined,
            happening_type: templateForm.happening_type,
            default_duration_minutes: templateForm.default_duration_minutes,
            required_roles: templateForm.required_roles,
            metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        });
    };

    const handleUpdateTemplate = (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingTemplate) return;
        const metadata: Record<string, unknown> = { ...(editingTemplate.metadata || {}) };
        if (templateForm.happening_type === 'show' && templateForm.default_show_type) {
            metadata.default_show_type = templateForm.default_show_type;
        } else if (templateForm.happening_type !== 'show') {
            // Remove default_show_type if not a show template
            delete metadata.default_show_type;
        }
        if (templateForm.default_place_id) {
            metadata.place_id = templateForm.default_place_id;
        } else {
            delete metadata.place_id;
        }
        if (templateForm.default_artists.length > 0) {
            metadata.default_artists = templateForm.default_artists;
        } else {
            delete metadata.default_artists;
        }
        // Save reminder defaults
        metadata.default_completion_reminder_offsets = templateForm.default_completion_reminder_offsets;
        metadata.default_happening_reminder_offsets = templateForm.default_happening_reminder_offsets;
        metadata.default_profile_reminder_offsets = templateForm.default_profile_reminder_offsets;
        metadata.default_post_show_enabled = templateForm.default_post_show_enabled;
        metadata.default_post_show_offset_hours = templateForm.default_post_show_offset_hours;
        updateMutation.mutate({
            id: editingTemplate.id,
            data: {
                name: templateForm.name,
                description: templateForm.description || null,
                happening_type: templateForm.happening_type,
                default_duration_minutes: templateForm.default_duration_minutes,
                required_roles: templateForm.required_roles,
                metadata,
            },
        });
    };

    const handleCreateTaskDef = (e: React.FormEvent) => {
        e.preventDefault();
        if (!expandedTemplateId) return;
        createTaskDefMutation.mutate({
            template_id: expandedTemplateId,
            task_key: taskDefForm.task_key || slugify(taskDefForm.title),
            title: taskDefForm.title,
            description: taskDefForm.description || undefined,
            type: taskDefForm.type,
            priority: taskDefForm.priority,
            due_offset_days: taskDefForm.due_offset_days,
            assignee_role: taskDefForm.assignee_role || undefined,
            is_required: taskDefForm.is_required,
            reminder_enabled: taskDefForm.reminder_enabled,
            reminder_offset_hours: taskDefForm.reminder_offset_hours,
            sort_order: taskDefinitions.length,
        });
    };

    const handleUpdateTaskDef = (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingTaskDef) return;
        updateTaskDefMutation.mutate({
            id: editingTaskDef.id,
            data: {
                title: taskDefForm.title,
                description: taskDefForm.description || null,
                type: taskDefForm.type,
                priority: taskDefForm.priority,
                due_offset_days: taskDefForm.due_offset_days,
                assignee_role: taskDefForm.assignee_role || null,
                is_required: taskDefForm.is_required,
                reminder_enabled: taskDefForm.reminder_enabled,
                reminder_offset_hours: taskDefForm.reminder_offset_hours,
            },
        });
    };

    const openEditTemplate = (template: HappeningTemplate) => {
        setEditingTemplate(template);
        const defaultShowType = template.happening_type === 'show' && template.metadata?.default_show_type
            ? (template.metadata.default_show_type as RadioShowType)
            : undefined;
        const defaultPlaceId = (template.metadata?.place_id as string) || '';
        const defaultArtists = (template.metadata?.default_artists as HappeningArtist[]) || [];
        const completionOffsets = (template.metadata?.default_completion_reminder_offsets as number[]) || [-120];
        const happeningOffsets = (template.metadata?.default_happening_reminder_offsets as number[]) || [-168];
        const profileOffsets = (template.metadata?.default_profile_reminder_offsets as number[]) || [-120];
        const postShowEnabled = template.metadata?.default_post_show_enabled !== false; // default true
        const postShowOffsetHours = (template.metadata?.default_post_show_offset_hours as number) || 24;
        setTemplateForm({
            slug: template.slug,
            name: template.name,
            description: template.description || '',
            happening_type: template.happening_type,
            default_duration_minutes: template.default_duration_minutes,
            required_roles: template.required_roles || [],
            default_show_type: defaultShowType,
            default_place_id: defaultPlaceId,
            default_artists: defaultArtists,
            default_completion_reminder_offsets: completionOffsets,
            default_happening_reminder_offsets: happeningOffsets,
            default_profile_reminder_offsets: profileOffsets,
            default_post_show_enabled: postShowEnabled,
            default_post_show_offset_hours: postShowOffsetHours,
        });
        templateInitialRef.current = {
            slug: template.slug,
            name: template.name,
            description: template.description || '',
            happening_type: template.happening_type,
            default_duration_minutes: template.default_duration_minutes,
            required_roles: template.required_roles || [],
            default_show_type: defaultShowType,
            default_place_id: defaultPlaceId,
            default_artists: defaultArtists,
            default_completion_reminder_offsets: completionOffsets,
            default_happening_reminder_offsets: happeningOffsets,
            default_profile_reminder_offsets: profileOffsets,
            default_post_show_enabled: postShowEnabled,
            default_post_show_offset_hours: postShowOffsetHours,
        };
    };

    const openEditTaskDef = (taskDef: TaskDefinition) => {
        setEditingTaskDef(taskDef);
        setTaskDefForm({
            task_key: taskDef.task_key,
            title: taskDef.title,
            description: taskDef.description || '',
            type: taskDef.type,
            priority: taskDef.priority,
            due_offset_days: taskDef.due_offset_days,
            assignee_role: taskDef.assignee_role || '',
            is_required: taskDef.is_required,
            reminder_enabled: taskDef.reminder_enabled ?? true,
            reminder_offset_hours: taskDef.reminder_offset_hours ?? 0,
        });
        taskInitialRef.current = {
            task_key: taskDef.task_key,
            title: taskDef.title,
            description: taskDef.description || '',
            type: taskDef.type,
            priority: taskDef.priority,
            due_offset_days: taskDef.due_offset_days,
            assignee_role: taskDef.assignee_role || '',
            is_required: taskDef.is_required,
            reminder_enabled: taskDef.reminder_enabled ?? true,
            reminder_offset_hours: taskDef.reminder_offset_hours ?? 0,
        };
        setShowTaskDefDialog(true);
    };

    const addRole = () => {
        if (!newRoleInput.role.trim()) return;
        const maxCount = newRoleInput.max_count === '' || newRoleInput.max_count === 0
            ? null
            : Number(newRoleInput.max_count);
        setTemplateForm((prev) => ({
            ...prev,
            required_roles: [
                ...prev.required_roles,
                {
                    role: newRoleInput.role.trim(),
                    min_count: Number(newRoleInput.min_count) || 1,
                    max_count: maxCount,
                    person_type: newRoleInput.person_type as any,
                },
            ],
        }));
        setNewRoleInput({ role: '', person_type: newRoleInput.person_type, min_count: 1, max_count: '' });
        // Focus back on role input for quick multi-add
        setTimeout(() => {
            document.getElementById('role-name-input')?.focus();
        }, 0);
    };

    const removeRole = (index: number) => {
        setTemplateForm((prev) => ({
            ...prev,
            required_roles: prev.required_roles.filter((_, i) => i !== index),
        }));
    };

    const updateRole = (index: number, field: string, value: any) => {
        setTemplateForm((prev) => ({
            ...prev,
            required_roles: prev.required_roles.map((role, i) =>
                i === index ? { ...role, [field]: value } : role
            ),
        }));
    };

    const getTypeIcon = (type: HappeningType) => {
        const found = HAPPENING_TYPES.find((t) => t.value === type);
        const Icon = found?.icon || FileText;
        return <Icon className="h-4 w-4" />;
    };

    const getDueOffsetLabel = (days: number) => {
        if (days === 0) return 'Day of event';
        if (days < 0) return `${Math.abs(days)} days before`;
        return `${days} days after`;
    };

    const isTemplateDirty = useMemo(() => {
        if (!templateInitialRef.current) return false;
        return JSON.stringify(templateForm) !== JSON.stringify(templateInitialRef.current);
    }, [templateForm]);

    const isTaskDefDirty = useMemo(() => {
        if (!taskInitialRef.current) return false;
        return JSON.stringify(taskDefForm) !== JSON.stringify(taskInitialRef.current);
    }, [taskDefForm]);

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Templates</h1>
                    <p className="text-muted-foreground">
                        Configure reusable templates for happenings with pre-defined roles and tasks
                    </p>
                </div>
                <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Template
                </Button>
            </div>

            <SearchFilter value={searchQuery} onChange={setSearchQuery} placeholder="Search templates..." />

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
            ) : filteredTemplates.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Layers className="h-12 w-12 text-muted-foreground/50" />
                        <p className="mt-4 text-lg font-medium">No templates yet</p>
                        <p className="text-sm text-muted-foreground">
                            Create your first template to streamline happening creation
                        </p>
                        <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Template
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {filteredTemplates.map((template) => (
                        <Card key={template.id} className={template.is_system ? 'border-primary/30' : ''}>
                            <CardHeader className="pb-2">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() =>
                                                setExpandedTemplateId(
                                                    expandedTemplateId === template.id ? null : template.id
                                                )
                                            }
                                            className="text-muted-foreground hover:text-foreground"
                                        >
                                            {expandedTemplateId === template.id ? (
                                                <ChevronDown className="h-5 w-5" />
                                            ) : (
                                                <ChevronRight className="h-5 w-5" />
                                            )}
                                        </button>
                                        <div className="flex items-center gap-2">
                                            {getTypeIcon(template.happening_type)}
                                            <CardTitle className="text-lg">{template.name}</CardTitle>
                                        </div>
                                        {template.is_system && (
                                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                                System
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditTemplate(template);
                                            }}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => deleteMutation.mutate(template.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                <CardDescription className="ml-8">
                                    {template.description || `/${template.slug}`}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="ml-8">
                                <div className="flex flex-wrap gap-4 text-sm">
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                        <Clock className="h-4 w-4" />
                                        {template.default_duration_minutes} min
                                    </div>
                                    {template.required_roles && template.required_roles.length > 0 && (
                                        <div className="flex items-center gap-1 text-muted-foreground">
                                            <Users className="h-4 w-4" />
                                            {template.required_roles.length} roles
                                        </div>
                                    )}
                                </div>

                                {/* Roles */}
                                {template.required_roles && template.required_roles.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {template.required_roles.map((role, idx) => (
                                            <span
                                                key={idx}
                                                className="rounded-full border px-2 py-0.5 text-xs"
                                            >
                                                {role.role}
                                                {role.min_count > 0 && (
                                                    <span className="ml-1 text-muted-foreground">
                                                        ({role.min_count}
                                                        {role.max_count ? `-${role.max_count}` : '+'})
                                                    </span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Default Artists */}
                                {template.metadata?.default_artists && (template.metadata.default_artists as any[]).length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-xs text-muted-foreground mb-2">Default Artists:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(template.metadata.default_artists as HappeningArtist[]).map((artist) => (
                                                <span
                                                    key={artist.person_id}
                                                    className="rounded-full bg-primary/10 px-2 py-0.5 text-xs"
                                                >
                                                    {artist.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Default Reminders Summary */}
                                {(() => {
                                    const meta = template.metadata || {};
                                    const completionOffsets = (meta.default_completion_reminder_offsets as number[]) || [];
                                    const happeningOffsets = (meta.default_happening_reminder_offsets as number[]) || [];
                                    const profileOffsets = (meta.default_profile_reminder_offsets as number[]) || [];
                                    const postShowOn = meta.default_post_show_enabled !== false;
                                    const hasReminders = completionOffsets.length > 0 || happeningOffsets.length > 0 || profileOffsets.length > 0 || postShowOn;
                                    if (!hasReminders) return null;
                                    return (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {completionOffsets.length > 0 && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                                                    <Mail className="h-3 w-3" />
                                                    Completion ({completionOffsets.length})
                                                </span>
                                            )}
                                            {happeningOffsets.length > 0 && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs text-green-700 dark:text-green-300">
                                                    <Clock className="h-3 w-3" />
                                                    Show ({happeningOffsets.length})
                                                </span>
                                            )}
                                            {profileOffsets.length > 0 && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 text-xs text-purple-700 dark:text-purple-300">
                                                    <UserIcon className="h-3 w-3" />
                                                    Profile ({profileOffsets.length})
                                                </span>
                                            )}
                                            {postShowOn && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 text-xs text-orange-700 dark:text-orange-300">
                                                    <Mail className="h-3 w-3" />
                                                    Post-Show
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Task Definitions (expanded) */}
                                {expandedTemplateId === template.id && (
                                    <div className="mt-4 space-y-3 border-t pt-4">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium">Task Definitions</p>
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setShowCopyTasksDialog(true)}
                                                >
                                                    <Layers className="mr-1 h-3 w-3" />
                                                    Copy from Template
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setTaskDefForm(defaultTaskDefForm);
                                                        setEditingTaskDef(null);
                                                        setShowTaskDefDialog(true);
                                                    }}
                                                >
                                                    <Plus className="mr-1 h-3 w-3" />
                                                    Add Task
                                                </Button>
                                            </div>
                                        </div>

                                        {sortedTaskDefinitions.length === 0 ? (
                                            <p className="text-sm text-muted-foreground italic">
                                                No task definitions yet. Add tasks that will be auto-created.
                                            </p>
                                        ) : (
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Task</TableHead>
                                                        <TableHead>Type</TableHead>
                                                        <TableHead>Due</TableHead>
                                                        <TableHead>Assignee Role</TableHead>
                                                        <TableHead>Reminder</TableHead>
                                                        <TableHead></TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {sortedTaskDefinitions.map((taskDef) => (
                                                        <TableRow key={taskDef.id}>
                                                            <TableCell>
                                                                <div>
                                                                    <p className="font-medium">{taskDef.title}</p>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        {taskDef.task_key}
                                                                    </p>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                                                                    {taskDef.type}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell>
                                                                {getDueOffsetLabel(taskDef.due_offset_days)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {taskDef.assignee_role || '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {!taskDef.reminder_enabled ? (
                                                                    <span className="text-xs text-muted-foreground">No reminder</span>
                                                                ) : taskDef.reminder_offset_hours === 0 ? (
                                                                    <span className="text-xs">On due date</span>
                                                                ) : taskDef.reminder_offset_hours < 0 ? (
                                                                    <span className="text-xs">{Math.abs(taskDef.reminder_offset_hours)}h before</span>
                                                                ) : (
                                                                    <span className="text-xs">{taskDef.reminder_offset_hours}h after</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="flex gap-1">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => openEditTaskDef(taskDef)}
                                                                    >
                                                                        <Edit className="h-3 w-3" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            deleteTaskDefMutation.mutate(taskDef.id)
                                                                        }
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create/Edit Template Dialog */}
            <Dialog
                open={showCreateDialog || !!editingTemplate}
                onOpenChange={(open) => {
                    if (!open && isTemplateDirty) {
                        setShowTemplateUnsavedConfirm(true);
                        return;
                    }
                    if (!open) {
                        setShowCreateDialog(false);
                        setEditingTemplate(null);
                        setTemplateForm(defaultTemplateForm);
                        templateInitialRef.current = null;
                    } else if (!editingTemplate) {
                        templateInitialRef.current = defaultTemplateForm();
                    }
                }}
            >
                <DialogContent size="large" className="max-h-[90vh] overflow-y-auto flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Template'}</DialogTitle>
                        <DialogDescription>
                            {editingTemplate
                                ? 'Update the template configuration'
                                : 'Create a reusable template for happenings'}
                        </DialogDescription>
                    </DialogHeader>
                    <form
                        onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                        onKeyDown={(e) => {
                            // Cmd/Ctrl+Enter submits the form
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                e.preventDefault();
                                const form = e.currentTarget;
                                form.requestSubmit();
                                return;
                            }
                            // Allow Enter in textareas (for new lines)
                            if (e.key === 'Enter' && e.target instanceof HTMLTextAreaElement) {
                                return;
                            }
                            // Block regular Enter on other inputs (prevents accidental form submit)
                            // Note: role input fields have their own onKeyDown that calls addRole()
                            if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
                                e.preventDefault();
                            }
                        }}
                    >
                        <div className="space-y-4 px-4 sm:px-6 py-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Name</label>
                                <input
                                    value={templateForm.name}
                                    onChange={(e) =>
                                        setTemplateForm((prev) => ({ ...prev, name: e.target.value }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                    placeholder="Guest Radio Show"
                                    required
                                />
                            </div>

                            {!editingTemplate && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Slug</label>
                                    <input
                                        value={templateForm.slug}
                                        onChange={(e) =>
                                            setTemplateForm((prev) => ({ ...prev, slug: e.target.value }))
                                        }
                                        className="w-full rounded-md border px-3 py-2"
                                        placeholder="guest-radio-show (auto-generated if empty)"
                                    />
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Type</label>
                                <select
                                    value={templateForm.happening_type}
                                    onChange={(e) =>
                                        setTemplateForm((prev) => ({
                                            ...prev,
                                            happening_type: e.target.value as HappeningType,
                                            // Clear show_type if not a show template
                                            default_show_type: e.target.value === 'show' ? prev.default_show_type : undefined,
                                        }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                >
                                    {HAPPENING_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Show Type (only for 'show' templates) */}
                            {templateForm.happening_type === 'show' && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Show Type</label>
                                    <select
                                        value={templateForm.default_show_type || ''}
                                        onChange={(e) =>
                                            setTemplateForm((prev) => ({
                                                ...prev,
                                                default_show_type: e.target.value ? (e.target.value as RadioShowType) : undefined,
                                            }))
                                        }
                                        className="w-full rounded-md border px-3 py-2"
                                    >
                                        <option value="">Select show type...</option>
                                        <option value="resident">Resident Show</option>
                                        <option value="guest">Guest Show</option>
                                        <option value="takeover">Takeover</option>
                                    </select>
                                    <p className="text-xs text-muted-foreground">
                                        The default show type that will be used when creating happenings from this template.
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Description</label>
                                <textarea
                                    value={templateForm.description}
                                    onChange={(e) =>
                                        setTemplateForm((prev) => ({ ...prev, description: e.target.value }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                    rows={2}
                                    placeholder="Template description..."
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Default Duration (minutes)</label>
                                <NumberInput
                                    allowEmpty={false}
                                    value={templateForm.default_duration_minutes}
                                    onChange={(n) =>
                                        setTemplateForm((prev) => ({
                                            ...prev,
                                            default_duration_minutes: n ?? 60,
                                        }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                    min={15}
                                    step={15}
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Default Place</label>
                                <select
                                    value={templateForm.default_place_id}
                                    onChange={(e) =>
                                        setTemplateForm((prev) => ({
                                            ...prev,
                                            default_place_id: e.target.value,
                                        }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                >
                                    <option value="">No default place</option>
                                    {places.map((place) => (
                                        <option key={place.id} value={place.id}>
                                            {place.name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted-foreground">
                                    Optional default location applied when creating happenings from this template.
                                </p>
                            </div>

                            {/* Default Artists (for resident shows) */}
                            {templateForm.happening_type === 'show' && templateForm.default_show_type === 'resident' && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Default Artists</label>
                                    <select
                                        onChange={(e) => {
                                            const personId = e.target.value;
                                            if (!personId) return;
                                            const person = people.find(p => p.id === personId);
                                            if (!person) return;
                                            const alreadyAdded = templateForm.default_artists.some(a => a.person_id === person.id);
                                            if (alreadyAdded) return;
                                            setTemplateForm((prev) => ({
                                                ...prev,
                                                default_artists: [...prev.default_artists, {
                                                    person_id: person.id,
                                                    name: person.name,
                                                    slug: person.slug,
                                                    picture_url: person.picture_url || null,
                                                }],
                                            }));
                                            e.target.value = ''; // Reset select
                                        }}
                                        className="w-full rounded-md border px-3 py-2"
                                        value=""
                                    >
                                        <option value="">Add artist...</option>
                                        {people.filter(p => !templateForm.default_artists.some(a => a.person_id === p.id)).map((person) => (
                                            <option key={person.id} value={person.id}>
                                                {person.name}
                                            </option>
                                        ))}
                                    </select>
                                    {templateForm.default_artists.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {templateForm.default_artists.map((artist) => (
                                                <span
                                                    key={artist.person_id}
                                                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm"
                                                >
                                                    {artist.name}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setTemplateForm((prev) => ({
                                                                ...prev,
                                                                default_artists: prev.default_artists.filter(a => a.person_id !== artist.person_id),
                                                            }));
                                                        }}
                                                        className="ml-1 rounded-full hover:bg-primary/20"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                        Artists that will be automatically added to resident shows created from this template.
                                    </p>
                                </div>
                            )}

                            {/* Default Reminder Settings */}
                            <div className="space-y-4 rounded-lg border border-input bg-muted/30 p-4">
                                <h4 className="text-sm font-medium flex items-center gap-2">
                                    <Bell className="h-4 w-4" />
                                    Default Reminder Settings
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                    These reminders will be pre-configured when creating shows from this template.
                                </p>

                                {/* Completion Reminders */}
                                <div className="space-y-2">
                                    <label className="text-xs font-medium flex items-center gap-2">
                                        <Mail className="h-3 w-3" />
                                        Completion Reminders
                                    </label>
                                    <div className="space-y-1">
                                        {templateForm.default_completion_reminder_offsets.map((offset, idx) => (
                                            <div key={idx} className="flex items-center gap-2 p-1.5 bg-background rounded border text-xs">
                                                <Clock className="h-3 w-3 text-muted-foreground" />
                                                <span className="flex-1">{formatOffsetHours(offset)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setTemplateForm(prev => ({
                                                        ...prev,
                                                        default_completion_reminder_offsets: prev.default_completion_reminder_offsets.filter((_, i) => i !== idx),
                                                    }))}
                                                    className="text-muted-foreground hover:text-destructive"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <select
                                            id="template-completion-offset"
                                            className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs"
                                            defaultValue="-120"
                                        >
                                            {BEFORE_SHOW_OFFSET_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="text-xs h-7"
                                            onClick={() => {
                                                const select = document.getElementById('template-completion-offset') as HTMLSelectElement;
                                                const val = parseInt(select.value, 10);
                                                if (!templateForm.default_completion_reminder_offsets.includes(val)) {
                                                    setTemplateForm(prev => ({
                                                        ...prev,
                                                        default_completion_reminder_offsets: [...prev.default_completion_reminder_offsets, val],
                                                    }));
                                                }
                                            }}
                                        >
                                            <Plus className="h-3 w-3 mr-1" />
                                            Add
                                        </Button>
                                    </div>
                                </div>

                                {/* Show Reminders */}
                                <div className="space-y-2">
                                    <label className="text-xs font-medium flex items-center gap-2">
                                        <Clock className="h-3 w-3" />
                                        Show Reminders
                                    </label>
                                    <div className="space-y-1">
                                        {templateForm.default_happening_reminder_offsets.map((offset, idx) => (
                                            <div key={idx} className="flex items-center gap-2 p-1.5 bg-background rounded border text-xs">
                                                <Clock className="h-3 w-3 text-muted-foreground" />
                                                <span className="flex-1">{formatOffsetHours(offset)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setTemplateForm(prev => ({
                                                        ...prev,
                                                        default_happening_reminder_offsets: prev.default_happening_reminder_offsets.filter((_, i) => i !== idx),
                                                    }))}
                                                    className="text-muted-foreground hover:text-destructive"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <select
                                            id="template-happening-offset"
                                            className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs"
                                            defaultValue="-168"
                                        >
                                            {BEFORE_SHOW_OFFSET_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="text-xs h-7"
                                            onClick={() => {
                                                const select = document.getElementById('template-happening-offset') as HTMLSelectElement;
                                                const val = parseInt(select.value, 10);
                                                if (!templateForm.default_happening_reminder_offsets.includes(val)) {
                                                    setTemplateForm(prev => ({
                                                        ...prev,
                                                        default_happening_reminder_offsets: [...prev.default_happening_reminder_offsets, val],
                                                    }));
                                                }
                                            }}
                                        >
                                            <Plus className="h-3 w-3 mr-1" />
                                            Add
                                        </Button>
                                    </div>
                                </div>

                                {/* Profile Reminders */}
                                <div className="space-y-2">
                                    <label className="text-xs font-medium flex items-center gap-2">
                                        <UserIcon className="h-3 w-3" />
                                        Artist Profile Reminders
                                    </label>
                                    <div className="space-y-1">
                                        {templateForm.default_profile_reminder_offsets.map((offset, idx) => (
                                            <div key={idx} className="flex items-center gap-2 p-1.5 bg-background rounded border text-xs">
                                                <Clock className="h-3 w-3 text-muted-foreground" />
                                                <span className="flex-1">{formatOffsetHours(offset)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setTemplateForm(prev => ({
                                                        ...prev,
                                                        default_profile_reminder_offsets: prev.default_profile_reminder_offsets.filter((_, i) => i !== idx),
                                                    }))}
                                                    className="text-muted-foreground hover:text-destructive"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <select
                                            id="template-profile-offset"
                                            className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs"
                                            defaultValue="-120"
                                        >
                                            {BEFORE_SHOW_OFFSET_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="text-xs h-7"
                                            onClick={() => {
                                                const select = document.getElementById('template-profile-offset') as HTMLSelectElement;
                                                const val = parseInt(select.value, 10);
                                                if (!templateForm.default_profile_reminder_offsets.includes(val)) {
                                                    setTemplateForm(prev => ({
                                                        ...prev,
                                                        default_profile_reminder_offsets: [...prev.default_profile_reminder_offsets, val],
                                                    }));
                                                }
                                            }}
                                        >
                                            <Plus className="h-3 w-3 mr-1" />
                                            Add
                                        </Button>
                                    </div>
                                </div>

                                {/* Post-Show Email */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-medium flex items-center gap-2">
                                            <Mail className="h-3 w-3" />
                                            Post-Show Email
                                        </label>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={templateForm.default_post_show_enabled}
                                                onChange={(e) => setTemplateForm(prev => ({
                                                    ...prev,
                                                    default_post_show_enabled: e.target.checked,
                                                }))}
                                                className="sr-only peer"
                                            />
                                            <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                    {templateForm.default_post_show_enabled && (
                                        <select
                                            value={templateForm.default_post_show_offset_hours}
                                            onChange={(e) => setTemplateForm(prev => ({
                                                ...prev,
                                                default_post_show_offset_hours: parseInt(e.target.value, 10),
                                            }))}
                                            className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                                        >
                                            {POST_SHOW_OFFSET_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>

                            {/* Required Roles */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Required Roles</label>
                                <p className="text-xs text-muted-foreground">
                                    Define who's needed for this type of happening
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <input
                                        value={newRoleInput.role}
                                        onChange={(e) =>
                                            setNewRoleInput((prev) => ({ ...prev, role: e.target.value }))
                                        }
                                        id="role-name-input"
                                        className="flex-1 min-w-[120px] rounded-md border px-3 py-2"
                                        placeholder="Role name"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addRole();
                                            }
                                        }}
                                    />
                                    <select
                                        value={newRoleInput.person_type}
                                        onChange={(e) =>
                                            setNewRoleInput((prev) => ({ ...prev, person_type: e.target.value }))
                                        }
                                        className="rounded-md border px-3 py-2"
                                    >
                                        {PERSON_TYPES.map((pt) => (
                                            <option key={pt} value={pt}>
                                                {pt}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex items-center gap-1">
                                        <NumberInput
                                            allowEmpty={false}
                                            value={newRoleInput.min_count}
                                            onChange={(n) =>
                                                setNewRoleInput((prev) => ({ ...prev, min_count: n ?? 0 }))
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addRole();
                                                }
                                            }}
                                            className="w-14 rounded-md border px-2 py-2 text-center"
                                            placeholder="Min"
                                            min={0}
                                            title="Minimum required"
                                        />
                                        <span className="text-muted-foreground">-</span>
                                        <input
                                            type="number"
                                            value={newRoleInput.max_count}
                                            onChange={(e) =>
                                                setNewRoleInput((prev) => ({ ...prev, max_count: e.target.value === '' ? '' : Number(e.target.value) }))
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addRole();
                                                }
                                            }}
                                            className="w-14 rounded-md border px-2 py-2 text-center"
                                            placeholder="∞"
                                            min={0}
                                            title="Maximum allowed (empty = unlimited)"
                                        />
                                    </div>
                                    <Button type="button" variant="outline" onClick={addRole}>
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                                {templateForm.required_roles.length > 0 && (
                                    <div className="space-y-2 pt-2 border-t">
                                        {templateForm.required_roles.map((role, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-2 text-sm"
                                            >
                                                <input
                                                    value={role.role}
                                                    onChange={(e) => updateRole(idx, 'role', e.target.value)}
                                                    className="flex-1 rounded border px-2 py-1"
                                                />
                                                <select
                                                    value={role.person_type}
                                                    onChange={(e) => updateRole(idx, 'person_type', e.target.value)}
                                                    className="rounded border px-2 py-1 text-xs"
                                                >
                                                    {PERSON_TYPES.map((pt) => (
                                                        <option key={pt} value={pt}>{pt}</option>
                                                    ))}
                                                </select>
                                                <NumberInput
                                                    allowEmpty={false}
                                                    value={role.min_count}
                                                    onChange={(n) => updateRole(idx, 'min_count', n ?? 0)}
                                                    className="w-12 rounded border px-2 py-1 text-center"
                                                    min={0}
                                                />
                                                <span className="text-muted-foreground">-</span>
                                                <input
                                                    type="number"
                                                    value={role.max_count ?? ''}
                                                    onChange={(e) => updateRole(idx, 'max_count', e.target.value === '' ? null : Number(e.target.value))}
                                                    className="w-12 rounded border px-2 py-1 text-center"
                                                    placeholder="∞"
                                                    min={0}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeRole(idx)}
                                                    className="text-muted-foreground hover:text-destructive"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <DialogFooter className="sticky bottom-0 left-0 right-0 bg-background border-t px-0 pt-4 gap-2 sm:justify-end">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateDialog(false);
                                    setEditingTemplate(null);
                                    templateInitialRef.current = null;
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={createMutation.isPending || updateMutation.isPending}
                                variant={isTemplateDirty ? 'default' : 'secondary'}
                            >
                                {isTemplateDirty ? 'Save changes (unsaved)' : editingTemplate ? 'Save Changes' : 'Create Template'}
                                <span className="ml-2 text-xs opacity-60">⌘↵</span>
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Unsaved changes confirm - Template */}
            <Dialog open={showTemplateUnsavedConfirm} onOpenChange={setShowTemplateUnsavedConfirm}>
                <DialogContent size="compact">
                    <DialogHeader>
                        <DialogTitle>Discard changes?</DialogTitle>
                        <DialogDescription>You have unsaved template changes. Close without saving?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:justify-end">
                        <Button variant="outline" onClick={() => setShowTemplateUnsavedConfirm(false)}>
                            Keep editing
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowTemplateUnsavedConfirm(false);
                                setShowCreateDialog(false);
                                setEditingTemplate(null);
                                setTemplateForm(defaultTemplateForm());
                                templateInitialRef.current = null;
                            }}
                        >
                            Discard
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Unsaved changes confirm - Task */}
            <Dialog open={showTaskUnsavedConfirm} onOpenChange={setShowTaskUnsavedConfirm}>
                <DialogContent size="compact">
                    <DialogHeader>
                        <DialogTitle>Discard changes?</DialogTitle>
                        <DialogDescription>You have unsaved task changes. Close without saving?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:justify-end">
                        <Button variant="outline" onClick={() => setShowTaskUnsavedConfirm(false)}>
                            Keep editing
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowTaskUnsavedConfirm(false);
                                setShowTaskDefDialog(false);
                                setEditingTaskDef(null);
                                setTaskDefForm(defaultTaskDefForm());
                                taskInitialRef.current = null;
                            }}
                        >
                            Discard
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create/Edit Task Definition Dialog */}
            <Dialog
                open={showTaskDefDialog}
                onOpenChange={(open) => {
                    if (!open && isTaskDefDirty) {
                        setShowTaskUnsavedConfirm(true);
                        return;
                    }
                    if (!open) {
                        setShowTaskDefDialog(false);
                        setEditingTaskDef(null);
                        setTaskDefForm(defaultTaskDefForm);
                        taskInitialRef.current = null;
                    } else if (!editingTaskDef) {
                        taskInitialRef.current = defaultTaskDefForm();
                    }
                }}
            >
                <DialogContent size="large" className="max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{editingTaskDef ? 'Edit Task Definition' : 'New Task Definition'}</DialogTitle>
                        <DialogDescription>
                            Define a task that will be auto-created when using this template
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={editingTaskDef ? handleUpdateTaskDef : handleCreateTaskDef} className="flex flex-col flex-1 min-h-0">
                        <div className="space-y-4 py-4 flex-1 overflow-y-auto min-h-0">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Title</label>
                                <input
                                    value={taskDefForm.title}
                                    onChange={(e) =>
                                        setTaskDefForm((prev) => ({ ...prev, title: e.target.value }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                    placeholder="Confirm Booking"
                                    required
                                />
                            </div>

                            {!editingTaskDef && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Key</label>
                                    <input
                                        value={taskDefForm.task_key}
                                        onChange={(e) =>
                                            setTaskDefForm((prev) => ({ ...prev, task_key: e.target.value }))
                                        }
                                        className="w-full rounded-md border px-3 py-2"
                                        placeholder="confirm-booking (auto-generated if empty)"
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Type</label>
                                    <select
                                        value={taskDefForm.type}
                                        onChange={(e) =>
                                            setTaskDefForm((prev) => ({
                                                ...prev,
                                                type: e.target.value as TaskType,
                                            }))
                                        }
                                        className="w-full rounded-md border px-3 py-2"
                                    >
                                        {TASK_TYPES.map((type) => (
                                            <option key={type} value={type}>
                                                {type.replace('_', ' ')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Priority</label>
                                    <select
                                        value={taskDefForm.priority}
                                        onChange={(e) =>
                                            setTaskDefForm((prev) => ({
                                                ...prev,
                                                priority: e.target.value as TaskPriority,
                                            }))
                                        }
                                        className="w-full rounded-md border px-3 py-2"
                                    >
                                        {TASK_PRIORITIES.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Due Offset (days from event)</label>
                                <NumberInput
                                    allowEmpty={false}
                                    value={taskDefForm.due_offset_days}
                                    onChange={(n) =>
                                        setTaskDefForm((prev) => ({
                                            ...prev,
                                            due_offset_days: n ?? 0,
                                        }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Negative = before event, 0 = day of, positive = after
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Assignee Role</label>
                                <select
                                    value={taskDefForm.assignee_role}
                                    onChange={(e) =>
                                        setTaskDefForm((prev) => ({ ...prev, assignee_role: e.target.value }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                >
                                    <option value="">Unassigned</option>
                                    {(currentTemplateForTaskDef?.required_roles || []).map((role) => (
                                        <option key={role.role} value={role.role}>
                                            {role.role.replace(/_/g, ' ')}
                                            {role.assignee_source && ` (${role.assignee_source === 'users' ? 'Staff' : 'External'})`}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted-foreground">
                                    Tasks with this role will auto-assign when someone fills that role
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Description</label>
                                <textarea
                                    value={taskDefForm.description}
                                    onChange={(e) =>
                                        setTaskDefForm((prev) => ({ ...prev, description: e.target.value }))
                                    }
                                    className="w-full rounded-md border px-3 py-2"
                                    rows={2}
                                    placeholder="Task description..."
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="is_required"
                                    checked={taskDefForm.is_required}
                                    onChange={(e) =>
                                        setTaskDefForm((prev) => ({ ...prev, is_required: e.target.checked }))
                                    }
                                    className="rounded"
                                />
                                <label htmlFor="is_required" className="text-sm">
                                    Required (must complete before happening can finish)
                                </label>
                            </div>

                            {/* Reminder Configuration */}
                            <div className="space-y-3 border-t pt-4">
                                <div className="flex items-center gap-2">
                                    <Bell className="h-4 w-4 text-muted-foreground" />
                                    <label className="text-sm font-medium">Email Reminders</label>
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="reminder_enabled"
                                        checked={taskDefForm.reminder_enabled}
                                        onChange={(e) =>
                                            setTaskDefForm((prev) => ({ ...prev, reminder_enabled: e.target.checked }))
                                        }
                                        className="rounded"
                                    />
                                    <label htmlFor="reminder_enabled" className="text-sm">
                                        Send email reminder
                                    </label>
                                </div>

                                {taskDefForm.reminder_enabled && (
                                    <div className="space-y-2 pl-6">
                                        <label className="text-sm font-medium">Reminder Timing (hours)</label>
                                        <NumberInput
                                            allowEmpty={false}
                                            value={taskDefForm.reminder_offset_hours}
                                            onChange={(n) =>
                                                setTaskDefForm((prev) => ({
                                                    ...prev,
                                                    reminder_offset_hours: n ?? 0,
                                                }))
                                            }
                                            className="w-full rounded-md border px-3 py-2"
                                            placeholder="0"
                                        />
                                        <div className="text-xs text-muted-foreground space-y-1">
                                            <p>• Negative values = hours before due date (e.g., -24 = 1 day before)</p>
                                            <p>• 0 = on due date</p>
                                            <p>• Positive values = hours after due date (e.g., 24 = 1 day after)</p>
                                            {taskDefForm.reminder_offset_hours !== 0 && (
                                                <p className="font-medium text-foreground mt-2">
                                                    Reminder will be sent{' '}
                                                    {taskDefForm.reminder_offset_hours < 0
                                                        ? `${Math.abs(taskDefForm.reminder_offset_hours)} hours before`
                                                        : `${taskDefForm.reminder_offset_hours} hours after`}{' '}
                                                    the task due date
                                                </p>
                                            )}
                                            {taskDefForm.reminder_offset_hours === 0 && (
                                                <p className="font-medium text-foreground mt-2">
                                                    Reminder will be sent on the task due date
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <DialogFooter className="border-t px-0 pt-4 gap-2 sm:justify-end mt-auto">
                            <Button type="button" variant="outline" onClick={() => setShowTaskDefDialog(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={createTaskDefMutation.isPending || updateTaskDefMutation.isPending}
                                variant={isTaskDefDirty ? 'default' : 'secondary'}
                            >
                                {isTaskDefDirty ? 'Save changes (unsaved)' : editingTaskDef ? 'Save Changes' : 'Add Task'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Copy Tasks Dialog */}
            <Dialog open={showCopyTasksDialog} onOpenChange={setShowCopyTasksDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Copy Tasks from Another Template</DialogTitle>
                        <DialogDescription>
                            Select a template to copy its task definitions to {editingTemplate?.name}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Source Template</label>
                            <select
                                value={selectedSourceTemplate || ''}
                                onChange={(e) => setSelectedSourceTemplate(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="">Select a template...</option>
                                {templates
                                    .filter(t => t.id !== expandedTemplateId) // Exclude current template
                                    .map(template => (
                                        <option key={template.id} value={template.id}>
                                            {template.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => {
                            setShowCopyTasksDialog(false);
                            setSelectedSourceTemplate(null);
                        }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (expandedTemplateId && selectedSourceTemplate) {
                                    copyTasksMutation.mutate({
                                        sourceTemplateId: selectedSourceTemplate,
                                        targetTemplateId: expandedTemplateId,
                                    });
                                }
                            }}
                            disabled={!selectedSourceTemplate || copyTasksMutation.isPending}
                        >
                            {copyTasksMutation.isPending ? 'Copying...' : 'Copy Tasks'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
